import { Component, ViewChild, ElementRef } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { CommonModule, Location } from "@angular/common";
import { DataService } from "../../Services/data.service";
import { DialogService } from "../../Services/dialog.service";
import { environment } from "../../../environments/environment";
import { Observable, throwError } from "rxjs";
import { tap, catchError } from "rxjs/operators";

@Component({
  selector: "app-mobile-upload",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./mobile-upload.component.html",
  styleUrls: ["./mobile-upload.component.css"],
})
export class MobileUploadComponent {
  token = "";
  selectedFiles: File[] = [];
  taskId = "";
  isUploading = false;
  currentUploading = 0;
  overallPercent = 0;
  currentFileName = "";
  uploadingCompleted = false;
  apiUrl = environment.apiUrl;
  uploadAllowed = false;
  errorMessage = "";
  isValidating = true;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    public dataService: DataService,
    public dialogService: DialogService,
    private location: Location,
  ) {}

  @ViewChild("fileInput")
  fileInput?: ElementRef<HTMLInputElement>;

  validateSession(): Observable<any> {
    this.isValidating = true;
    return this.dataService.get(`/upload/session/${this.token}`).pipe(
      tap((res: any) => {
        this.taskId = res.taskId;
        this.uploadAllowed = true;
        this.errorMessage = "";
        this.isValidating = false;
      }),
      catchError((err) => {
        this.uploadAllowed = false;
        this.selectedFiles = [];
        this.isValidating = false;
        this.errorMessage =
          err?.error ||
          err?.error?.message ||
          "This QR code is no longer valid.";
        return throwError(() => err);
      }),
    );
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.token = params["token"];
      this.taskId = params["taskId"];
      this.validateSession().subscribe({
        next: () => {},
        error: () => {},
      });
    });
  }

  selectFiles(event: any) {
    if (!event.target.files) {
      return;
    }

    this.uploadingCompleted = false;
    this.currentUploading = 0;
    this.overallPercent = 0;
    this.currentFileName = "";

    this.selectedFiles = Array.from(event.target.files);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return "";

    const kb = 1024;
    const mb = kb * 1024;

    if (bytes >= mb) {
      return (bytes / mb).toFixed(2) + " MB";
    }

    return (bytes / kb).toFixed(2) + " KB";
  }

  upload() {
    if (!this.selectedFiles.length) {
      return;
    }

    this.isUploading = true;
    this.uploadingCompleted = false;
    this.overallPercent = 0;

    const uploadNext = (index: number) => {
      if (index >= this.selectedFiles.length) {
        this.isUploading = false;
        this.overallPercent = 100;
        this.uploadingCompleted = true;

        this.selectedFiles = [];
        this.currentFileName = "";
        this.currentUploading = 0;

        if (this.fileInput) {
          this.fileInput.nativeElement.value = "";
        }

        return;
      }

      this.currentUploading = index + 1;

      this.currentFileName = this.selectedFiles[index].name;

      this.overallPercent = Math.round(
        (index / this.selectedFiles.length) * 100,
      );

      const formData = new FormData();

      formData.append("file", this.selectedFiles[index]);

      this.validateSession().subscribe({
        next: () => {
          this.http
            .post(
              `${this.apiUrl}/upload/mobile/${this.token}?allow=allow`,
              formData,
              {
                responseType: "text",
              },
            )
            .subscribe({
              next: (res: string) => {
                if (res === "You are about to upload file with same content") {
                  this.isUploading = false;

                  this.dialogService
                    .openConfirmDialog(
                      "File already exists",
                      `${this.selectedFiles[index].name} already exists. Do you want to upload another copy?`,
                    )
                    .subscribe((confirmed: boolean) => {
                      if (confirmed) {
                        this.isUploading = true;

                        this.http
                          .post(
                            `${this.apiUrl}/upload/mobile/${this.token}?allow=allow`,
                            formData,
                            { responseType: "text" },
                          )
                          .subscribe({
                            next: () => {
                              this.overallPercent = Math.round(
                                ((index + 1) / this.selectedFiles.length) * 100,
                              );

                              uploadNext(index + 1);
                            },

                            error: (err) => {
                              console.error(err);
                              this.isUploading = false;
                            },
                          });
                      } else {
                        // Skip this file and continue with the next one
                        uploadNext(index + 1);
                      }
                    });

                  return;
                }

                this.overallPercent = Math.round(
                  ((index + 1) / this.selectedFiles.length) * 100,
                );

                console.log("Uploaded:", res);

                uploadNext(index + 1);
              },

              error: (err) => {
                console.error(err);

                if (err.status === 403) {
                  this.isUploading = false;
                  this.uploadAllowed = false;
                  this.errorMessage = err.error;
                  this.uploadingCompleted = false;
                  this.isValidating = false;
                  return;
                }
              },
            });
        },

        error: (err) => {
          this.isUploading = false;
          this.uploadAllowed = false;
          this.selectedFiles = [];
          this.currentFileName = "";
          this.currentUploading = 0;
          this.overallPercent = 0;
          this.uploadingCompleted = false;
          this.errorMessage = err.error;
          this.isValidating = false;
          this.errorMessage =
            err?.error || "Task is no longer accepting uploads.";
        },
      });
    };

    uploadNext(0);
  }
}
